import { GlobalStorage, storage } from '@otto-ec/global-resources/storage';
import { submitMerge } from '@otto-ec/tracking-bct';
import { cookie } from '@otto-ec/global-resources/cookie';
import { logger } from '@otto-ec/global-resources/debug';
import { CustomerSessionRefreshed, onCustomerSessionRefresh } from '@otto-ec/fti-customer-session-frontend';

class MiniWishlist {
    private readonly emptyBadgeClass: string;
    private storage: GlobalStorage;
    private readonly amountStorageKey: string;
    private readonly tooltip: HTMLOcTooltipV1Element | null;
    private readonly overlay: HTMLOcInteractiveOverlayV1Element | null;
    private readonly amountBadge: Element | null;
    private readonly amountBadgePresent: boolean;
    private readonly label: HTMLElement | null;
    private hasCustomerSession: boolean = false;
    private log = logger('ft1.wishlist.mini');

    constructor(private element: Element, private toggles = { toggleActive: (_toggleName: string) => false }) {
        const useNewIcon = toggles.toggleActive('FT1_32396_IMPROVE_HEADER_ICON_ACCESSIBILITY');
        this.emptyBadgeClass = useNewIcon ? 'wl_mini-wishlist__badge--empty' : 'wl_mini__badge--empty';
        this.storage = storage(window.localStorage);
        this.amountStorageKey = 'wl_miniWishlistAmount';
        if (this.element) {
            this.tooltip = useNewIcon ? (this.element as HTMLOcTooltipV1Element) : null;
            this.overlay = this.element.querySelector<HTMLOcInteractiveOverlayV1Element>('.wl_js_mini_overlay');
            this.amountBadge = this.element.querySelector('.wl_js_mini_amount');
            this.label = this.element.querySelector('.wl_js_mini_label');
        } else {
            this.tooltip = null;
            this.overlay = null;
            this.amountBadge = null;
            this.label = null;
        }
        this.amountBadgePresent = this.amountBadge !== null;
    }

    init() {
        if (this.amountBadgePresent) {
            window.document.addEventListener('wishlist.changed', async e => await this.onWishlistChanged(e as WishlistChangedEvent));

            /*                                    */
            window.addEventListener('storage', e => this.onStorageChange(e));

            /*                       */
            window.setTimeout(() => this.onPageLoad(), 10);
        }
        if (this.tooltip && this.label) {
            const isOverflowing = this.label.offsetWidth < this.label.scrollWidth;
            if (!isOverflowing) {
                this.tooltip.disabled = true;
            }
        }

        onCustomerSessionRefresh.subscribe((event: CustomerSessionRefreshed) => {
            this.hasCustomerSession = event.hasCustomerSession;
        });
        return this;
    }

    private async onPageLoad() {
        const savedAmount = this.getAmountFromStorage();
        if (savedAmount !== undefined) {
            this.renderAmount(savedAmount);
            this.trackAmountOnPageImpression(savedAmount);
        } else {
            await this.refreshAmount();
        }

        return this;
    }

    onWishlistChanged(event: WishlistChangedEvent) {
        if (event && event.detail && event.detail.newAmount) {
            const newAmount = event.detail.newAmount;
            if (this.amountBadgePresent) {
                this.renderAmount(newAmount);
            }
            this.saveToStorage(newAmount);
            return Promise.resolve();
        }
        return this.refreshAmount();
    }

    onStorageChange(storageEvent: StorageEvent) {
        if (storageEvent.key === this.amountStorageKey) {
            const amount = this.getAmountFromStorage(storageEvent.newValue);
            if (amount !== undefined) {
                this.renderAmount(amount);
            }
        }
    }

    getAmountFromStorage(currentValue?: string | null): number | undefined {
        let storageData: string | null;
        if (currentValue !== undefined) {
            storageData = currentValue;
        } else {
            storageData = this.storage.getItem(this.amountStorageKey);
        }
        return this.parseStorageData(storageData);
    }

    parseStorageData(dataString: string | null): number | undefined {
        if (!dataString) {
            return undefined;
        }
        try {
            const data = JSON.parse(dataString) as StoredAmountData;
            if (data && data.expire > new Date().getTime() && data.visitorId === this.getVisitorId() && data.loggedIn === this.isLoggedIn()) {
                return data.amount;
            }
            return undefined;
        } catch (e) {
            return undefined;
        }
    }

    saveToStorage(amount: number) {
        const data: StoredAmountData = {
            amount: amount,
            visitorId: this.getVisitorId(),
            expire: new Date().getTime() + 300000,
            loggedIn: this.isLoggedIn()
        };
        this.storage.setItem(this.amountStorageKey, JSON.stringify(data));
        return amount;
    }

    private refreshAmount() {
        return this.loadAmount().then(loadedAmount => {
            this.saveToStorage(loadedAmount);
            this.renderAmount(loadedAmount);
            this.trackAmountOnPageImpression(loadedAmount);
        });
    }

    private async loadAmount() {
        const amountUrl = this.amountBadge?.getAttribute('data-amount-url')!;
        const res = await fetch(amountUrl);
        const json = await res.json();
        return json.amount;
    }

    toggleBadge(showBadge: boolean) {
        if (this.amountBadgePresent) {
            this.amountBadge!.classList.toggle(this.emptyBadgeClass, !showBadge);
        }
    }

    updateAriaLabel(amount: number) {
        if (this.overlay) {
            let ariaLabel = 'Merkzettel';
            if (amount > 0) {
                ariaLabel += ` - ${amount} Artikel`;
            }

            this.overlay.ocAriaLabel = ariaLabel;
        }
    }

    renderAmount(amount: number) {
        if (this.amountBadge) {
            this.amountBadge.innerHTML = '' + amount;
        }
        this.toggleBadge(amount > 0);
        this.updateAriaLabel(amount);
    }

    getVisitorId() {
        return cookie.get('visitorId');
    }

    trackAmountOnPageImpression(amount: number) {
        submitMerge({
            wishlist_WishlistItems: [amount.toString()]
        });
    }

    isLoggedIn() {
        return this.hasCustomerSession;
    }
}

interface StoredAmountData {
    amount: number;
    visitorId: string | null;
    expire: number;
    loggedIn: boolean;
}

interface WishlistChangedEvent extends CustomEvent {
    detail: {
        newAmount: number;
    } | null;
}

export { MiniWishlist };
